import Note from '@/components/Note'
import { getErrorMessage } from '@/lib/error'
import WebPreview from '@/components/WebPreview'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { transformCustomEmojisInContent, trimTagEnd } from '@/lib/draft-event'
import { addPendingPublish, removePendingPublish, usePendingCountdown } from '@/lib/pendingPublish'
import { isTouchDevice, now } from '@/lib/utils'
import {
  getReplaceableCoordinateFromEvent,
  getRoot,
  isReplaceableEvent,
  isReplyNoteEvent
} from '@/lib/event'
import { addReplies } from '@/atoms/replies'
import { canWriteAtom, unwrappedSignerAtom, useCurrentPubkey } from '@/atoms/session'
import { hasUnsavedNsec, openSaveNsecModal, recordNotePublishedDay } from '@/atoms/key-backup'
import {
  addEventToCache,
  determineTargetRelays,
  emitEphemeralEvent,
  emitEventRemoved,
  getEventHint,
  getSeenEventRelayUrls,
  publishEventAndDisplay,
  removeEventFromCache
} from '@/services/client.service'
import { discardPendingEvent, savePendingEvent } from '@/atoms/pending'

import { HugeiconsIcon } from '@hugeicons/react'
import {
  EyeOffIcon,
  ImageUpload01Icon,
  Loading01Icon,
  SmileIcon,
  SlowWindsIcon,
  ShieldMinus,
  CrownIcon
} from '@hugeicons/core-free-icons'
import { NostrEvent } from '@nostr/tools/core'
import * as kinds from '@nostr/tools/kinds'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import EmojiPickerDialog from '../EmojiPickerDialog'
import PostTextarea, { TPostTextareaHandle } from './PostTextarea'
import Uploader from './Uploader'
import BlossomUploadChecklist from './BlossomUploadChecklist'
import {
  getImetaTagByUrl,
  type ServerStatus,
  type UploadFile
} from '@/services/media-upload.service'
import RelaySelector, { Item, SelectableItem } from '../RelaySelector'
import { normalizeUrl, simplifyUrl } from '@/lib/url'
import { installedSlashCommandsAtom } from '@/atoms/slash-commands'
import {
  getSlashCommandArgProblems,
  getSlashCommandRelayUrls,
  parseSlashCommandInvocation,
  slashCommandAppliesToEvent
} from '@/lib/slash-command-definition'
import { parse } from '@nostr/tools/nip27'
import { unique } from '@nostr/gadgets/utils'
import { invalidateFeed, invalidateReplies } from '@/atoms/invalidation'
import { unwrappedFavoriteRelaysWithSetsAtom, unwrappedRelayListAtom } from '@/atoms/lists'
import { useAtom, useAtomValue } from 'jotai'
import { getComposerStateAtom, removeComposerState, TComposerState } from './composer-state'
import { useAsync } from 'react-use'
import { loadRelayInfo } from '@nostr/gadgets/relays'
import { unwrappedIsPremiumPublisherAtom } from '@/atoms/premium'
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { isSmallScreenAtom } from '@/atoms/screenSize'
import SchedulePicker, { ScheduleButton } from './SchedulePicker'

const PENDING_PUBLISH_MS = 10_000

function waitForAbortableDelay(duration: number, signal: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException('Aborted', 'AbortError'))
      return
    }

    const timeout = window.setTimeout(() => {
      signal.removeEventListener('abort', handleAbort)
      resolve()
    }, duration)

    const handleAbort = () => {
      window.clearTimeout(timeout)
      signal.removeEventListener('abort', handleAbort)
      reject(new DOMException('Aborted', 'AbortError'))
    }

    signal.addEventListener('abort', handleAbort)
  })
}

function PendingPublishToast({ endAt, onUndo }: { endAt: number; onUndo: () => void }) {
  const secondsLeft = usePendingCountdown(endAt)
  const { t } = useTranslation()

  return (
    <div className="flex items-center gap-3 rounded-full bg-primary text-primary-foreground shadow hover:bg-primary-hover py-2 pl-4 pr-3">
      <div className="text-sm font-medium">{t('Changed your mind?')}</div>
      <div className="text-sm font-medium w-8 text-center">{secondsLeft}s</div>
      <Button variant="secondary" size="sm" onClick={onUndo}>
        {t('Cancel')}
      </Button>
    </div>
  )
}

export default function PostContent({
  defaultContent = '',
  editingEvent,
  parentEvents,
  parentExternal,
  close,
  open,
  openFromRelays,
  initialState
}: {
  defaultContent?: string
  editingEvent?: NostrEvent
  parentEvents?: NostrEvent[]
  parentExternal?: string
  close: () => void
  open: (content?: string, initialState?: TComposerState) => void
  openFromRelays?: string[]
  initialState?: TComposerState
}) {
  const { t } = useTranslation()
  const canWrite = useAtomValue(canWriteAtom)
  const signer = useAtomValue(unwrappedSignerAtom)
  const pubkey = useCurrentPubkey()
  const isSmallScreen = useAtomValue(isSmallScreenAtom)
  const [text, setText] = useState('')
  const textareaRef = useRef<TPostTextareaHandle>(null)
  const scheduleAnchorRef = useRef<HTMLButtonElement>(null)
  const [posting, setPosting] = useState(false)
  const [scheduleOpen, setScheduleOpen] = useState(false)
  const [uploadProgresses, setUploadProgresses] = useState<
    { file: UploadFile; cancel: () => void; servers: { url: string; status: ServerStatus }[] }[]
  >([])

  const seenOn =
    parentEvents && parentEvents.every((pe) => pe.tags.some(([t]) => t === '-'))
      ? parentEvents.flatMap((pe) => getSeenEventRelayUrls(pe.id, pe))
      : []

  const publishTargetItems = (window.hallway.universe.publishTargets ?? []).map(
    (url) => ({ kind: 'relay', url }) as const
  )
  const ourWriteRelaysInDefault = !openFromRelays?.length && seenOn.length === 0

  const composerStateAtom = getComposerStateAtom(
    defaultContent,
    parentEvents,
    openFromRelays,
    () => {
      const base: SelectableItem[] = openFromRelays?.length
        ? openFromRelays.map((url) => ({ kind: 'relay', url }))
        : seenOn.length
          ? seenOn.map((url) => ({ kind: 'relay', url }))
          : window.hallway.universe.publishTargetsExclusive
            ? []
            : parentEvents
              ? ['ourWriteRelays', 'theirReadRelays'].map((id) => ({
                  kind: 'item',
                  id,
                  label: ''
                }))
              : [{ kind: 'item', id: 'ourWriteRelays', label: '' }]

      return {
        nsfw: false,
        protected: window.hallway.universe.protectedDefault,
        premium: false,
        content: null,
        quietReply: false,
        relaySelection: ourWriteRelaysInDefault ? [...base, ...publishTargetItems] : base
      }
    },
    initialState
  )

  const [composerState, setComposerState] = useAtom(composerStateAtom)
  const [scheduledAt, setScheduledAt] = useState<number | null>(null)

  const hasSpecificRelays = composerState.relaySelection.some((item) => item.kind !== 'item')
  const hasOurWriteRelays = composerState.relaySelection.some(
    (item) => item.kind === 'item' && item.id === 'ourWriteRelays'
  )
  const isSpecificOnly = hasSpecificRelays && !hasOurWriteRelays
  const wasSpecificOnlyRef = useRef(false)

  useEffect(() => {
    if (isSpecificOnly && !wasSpecificOnlyRef.current) {
      setComposerState((curr) => (curr.protected ? curr : { ...curr, protected: true }))
    }
    wasSpecificOnlyRef.current = isSpecificOnly
  }, [isSpecificOnly, setComposerState])

  useEffect(() => {
    if (composerState.premium && !composerState.protected) {
      setComposerState((curr) => ({ ...curr, protected: true }))
    }
  }, [composerState.premium, composerState.protected, setComposerState])

  const canPost =
    !!pubkey &&
    canWrite &&
    !!text &&
    !posting &&
    !uploadProgresses.length &&
    (!composerState.protected || hasSpecificRelays) &&
    (!composerState.premium || hasSpecificRelays) &&
    (scheduledAt == null ||
      composerState.relaySelection.some(
        (item) => item.kind === 'relay' || item.kind === 'relay-set'
      ))

  const favorites = useAtomValue(unwrappedFavoriteRelaysWithSetsAtom)

  const writeRelays = (useAtomValue(unwrappedRelayListAtom) ?? []).filter((r) => r.write)
  const writeRelaysKey = writeRelays.map((r) => r.url).join('|')

  const { value: nip16WriteRelays } = useAsync(async (): Promise<string[]> => {
    if (writeRelays.length === 0) return []
    const infos = await Promise.all(writeRelays.map((r) => loadRelayInfo(r.url)))
    return writeRelays.filter((_, i) => infos[i]?.supported_nips?.includes?.(16)).map((r) => r.url)
  }, [writeRelaysKey])

  const favoriteRelayUrls = useMemo(() => {
    if (!favorites) return []
    const urls: string[] = []
    for (const f of favorites) {
      if (typeof f === 'string') urls.push(f)
      else urls.push(...f.items)
    }
    return unique(urls)
  }, [favorites])
  const favoriteRelayUrlsKey = favoriteRelayUrls.join('|')

  const { value: nip63WriteRelays } = useAsync(async (): Promise<string[]> => {
    if (writeRelays.length === 0) return []
    const infos = await Promise.all(writeRelays.map((r) => loadRelayInfo(r.url)))
    return writeRelays.filter((_, i) => infos[i]?.supported_nips?.includes?.(63)).map((r) => r.url)
  }, [writeRelaysKey])

  const { value: nip63FavoriteRelays } = useAsync(async (): Promise<string[]> => {
    if (favoriteRelayUrls.length === 0) return []
    const infos = await Promise.all(favoriteRelayUrls.map((url) => loadRelayInfo(url)))
    return favoriteRelayUrls.filter((_, i) => infos[i]?.supported_nips?.includes?.(63))
  }, [favoriteRelayUrlsKey])

  const isPremiumPublisher = useAtomValue(unwrappedIsPremiumPublisherAtom)

  const nip16Set = useMemo(() => new Set(nip16WriteRelays ?? []), [nip16WriteRelays])
  const isScheduled = scheduledAt != null
  const hasNip16Support = nip16Set.size > 0

  const hasExplicitMentions = useMemo(() => {
    if (parentEvents) return false
    for (const block of parse(text)) {
      if (block.type === 'reference') {
        if ('pubkey' in block.pointer && block.pointer.pubkey === pubkey) continue
        if ('author' in block.pointer && block.pointer.author === pubkey) continue
        return true
      }
    }
    return false
  }, [text, parentEvents])

  // --- slash command (Commands NIP) composer context -----------------------

  // live preview of the tags the composed event will carry, so that commands
  // with `tag:` scopes can be filtered while the user is still typing
  const slashTargetTags = useMemo(() => {
    const tags: string[][] = []
    const seen = new Set<string>()
    const addTag = (tag: string[]) => {
      const key = `${tag[0]}:${tag[1] ?? ''}`
      if (seen.has(key)) return
      seen.add(key)
      tags.push(tag)
    }

    const { content } = transformCustomEmojisInContent(text)
    for (const block of parse(content)) {
      switch (block.type) {
        case 'hashtag':
          addTag(['t', block.value])
          break
        case 'reference':
          if ('identifier' in block.pointer) {
            addTag([
              'q',
              `${block.pointer.kind}:${block.pointer.pubkey}:${block.pointer.identifier}`
            ])
          } else if ('id' in block.pointer) {
            addTag(['q', block.pointer.id])
          } else if (block.pointer.pubkey !== pubkey) {
            addTag(['p', block.pointer.pubkey])
          }
          break
      }
    }

    if (parentEvents) {
      for (const pe of parentEvents) {
        if (pe.pubkey !== pubkey) addTag(['p', pe.pubkey])
        addTag(['e', pe.id])
        if (isReplaceableEvent(pe.kind)) addTag(['a', getReplaceableCoordinateFromEvent(pe)])
      }
    }

    return tags
  }, [text, pubkey, parentEvents])

  // kind of the event that will actually be published (comment vs note)
  const slashTargetKind = useMemo(
    () =>
      parentEvents &&
      (parentEvents[0].kind !== kinds.ShortTextNote ||
        composerState.quietReply ||
        parentEvents.length > 1)
        ? kinds.Comment
        : kinds.ShortTextNote,
    [parentEvents, composerState.quietReply]
  )

  const slashComposerContext = useMemo(
    () => ({
      kind: slashTargetKind,
      tags: slashTargetTags,
      pubkey: pubkey ?? undefined
    }),
    [slashTargetKind, slashTargetTags, pubkey]
  )

  // the installed commands the content currently invokes (more than one when
  // the invocation isn't qualified with `@npub` and several share the trigger)
  const installedSlashCommands = useAtomValue(installedSlashCommandsAtom)
  const slashInvocation = useMemo(() => {
    const invocation = parseSlashCommandInvocation(text)
    if (!invocation) return null
    const slashCommands = installedSlashCommands.filter(
      (slashCommand) =>
        slashCommand.command === invocation.name &&
        (!invocation.pubkey || slashCommand.pubkey === invocation.pubkey) &&
        slashCommandAppliesToEvent(slashCommand, slashComposerContext)
    )
    return { slashCommands, rest: text.slice(invocation.length) }
  }, [text, installedSlashCommands, slashComposerContext])

  // advisory only: the content is sent anyway, as the NIP requires
  const slashArgProblems = useMemo(
    () =>
      slashInvocation?.slashCommands.length === 1
        ? getSlashCommandArgProblems(slashInvocation.slashCommands[0], slashInvocation.rest)
        : [],
    [slashInvocation]
  )

  // `relay:` scoped commands are only heard on those relays, so add them to
  // the selection while the command is in the content and take them back out
  // (only the ones we added) when it goes away
  const slashRelayUrlsKey = useMemo(
    () =>
      unique(
        (slashInvocation?.slashCommands ?? []).flatMap((slashCommand) =>
          getSlashCommandRelayUrls(slashCommand).map(normalizeUrl)
        )
      )
        .sort()
        .join(' '),
    [slashInvocation]
  )
  const slashAddedRelaysRef = useRef(new Set<string>())
  useEffect(() => {
    const wanted = slashRelayUrlsKey ? slashRelayUrlsKey.split(' ') : []
    const selected = new Set(
      composerState.relaySelection.flatMap((item) => (item.kind === 'relay' ? [item.url] : []))
    )
    const toRemove = [...slashAddedRelaysRef.current].filter((url) => !wanted.includes(url))
    const toAdd = wanted.filter((url) => !selected.has(url))
    slashAddedRelaysRef.current = new Set([
      ...[...slashAddedRelaysRef.current].filter((url) => wanted.includes(url)),
      ...toAdd
    ])
    if (toRemove.length === 0 && toAdd.length === 0) return

    setComposerState((curr) => ({
      ...curr,
      relaySelection: [
        ...curr.relaySelection.filter(
          (item) => !(item.kind === 'relay' && toRemove.includes(item.url))
        ),
        ...toAdd.map((url) => ({ kind: 'relay', url }) as const)
      ]
    }))
  }, [slashRelayUrlsKey])

  useEffect(() => {
    if (isScheduled) return
    setComposerState((curr) => {
      const hasTheirReadRelays = curr.relaySelection.some(
        (item) => item.kind === 'item' && item.id === 'theirReadRelays'
      )
      if (hasExplicitMentions && !hasTheirReadRelays) {
        return {
          ...curr,
          relaySelection: [
            ...curr.relaySelection,
            { kind: 'item', id: 'theirReadRelays', label: '' }
          ]
        }
      }
      if (!hasExplicitMentions && hasTheirReadRelays && !parentEvents) {
        return {
          ...curr,
          relaySelection: curr.relaySelection.filter(
            (item) => !(item.kind === 'item' && item.id === 'theirReadRelays')
          )
        }
      }
      return curr
    })
  }, [hasExplicitMentions, isScheduled, parentEvents, setComposerState])

  const initialSelected = useMemo(() => {
    const ids: string[] = []
    for (const item of composerState.relaySelection) {
      if (item.kind === 'relay') ids.push(item.url)
      else if (item.kind === 'item') ids.push(item.id)
      else if (item.kind === 'relay-set') ids.push(item.identifier)
    }
    return new Set(ids)
  }, [composerState.relaySelection])

  useEffect(() => {
    if (!isScheduled) return
    if (nip16Set.size === 0) return
    setComposerState((curr) => {
      const next: SelectableItem[] = []
      let hasOurWriteRelays = false
      let hasTheirReadRelays = false
      const explicit: string[] = []
      for (const item of curr.relaySelection) {
        if (item.kind === 'item' && item.id === 'ourWriteRelays') {
          hasOurWriteRelays = true
          continue
        }
        if (item.kind === 'item' && item.id === 'theirReadRelays') {
          hasTheirReadRelays = true
          continue
        }
        if (item.kind === 'relay') {
          if (nip16Set.has(item.url)) {
            if (!explicit.includes(item.url)) {
              explicit.push(item.url)
              next.push(item)
            }
          }
          continue
        }
        if (item.kind === 'relay-set') {
          const filtered = item.relays.filter((u) => nip16Set.has(u))
          if (filtered.length) {
            next.push({ ...item, relays: filtered })
          }
          continue
        }
      }
      if (hasOurWriteRelays || (explicit.length === 0 && !hasTheirReadRelays)) {
        for (const url of nip16Set) {
          if (!explicit.includes(url)) {
            explicit.push(url)
            next.push({ kind: 'relay', url })
          }
        }
      }
      if (
        next.length === curr.relaySelection.length &&
        next.every((n, i) => {
          const c = curr.relaySelection[i]
          if (!c) return false
          if (n.kind !== c.kind) return false
          if (n.kind === 'relay' && c.kind === 'relay') return n.url === c.url
          if (n.kind === 'relay-set' && c.kind === 'relay-set') {
            return n.identifier === c.identifier && n.relays.length === c.relays.length
          }
          return false
        })
      ) {
        return curr
      }
      return { ...curr, relaySelection: next }
    })
  }, [isScheduled, nip16Set, setComposerState])

  const relayOptions = useMemo(() => {
    if (isScheduled && nip16Set.size > 0) {
      const opts: Item[] = []
      const explicit = Array.from(nip16Set)
      if (explicit.length) {
        opts.push(...explicit.map((url) => ({ kind: 'relay' as const, url })))
      }
      return opts
    }

    const opts: Item[] = [{ kind: 'item', id: 'ourWriteRelays', label: t('Our outbox relays') }]
    const added: string[] = publishTargetItems.map((r) => r.url)

    if (parentEvents || hasExplicitMentions) {
      opts.push({ kind: 'item', id: 'theirReadRelays', label: t('Their inbox relays') })
    }

    {
      // favorites section
      opts.push({ kind: 'separator' })
      if (openFromRelays)
        for (const url of openFromRelays) {
          if (added.includes(url)) continue
          opts.push({
            kind: 'relay',
            url
          })
          added.push(url)
        }
      for (const url of seenOn) {
        if (added.includes(url)) continue
        opts.push({
          kind: 'relay',
          url
        })
        added.push(url)
      }
      if (favorites) {
        for (const fav of favorites) {
          if (typeof fav === 'string') {
            if (added.includes(fav)) continue
            opts.push({
              kind: 'relay',
              url: fav
            })
            added.push(fav)
          } else {
            opts.push({
              kind: 'relay-set',
              identifier: fav.pointer.identifier,
              relays: fav.items,
              name: fav.title
            })
          }
        }
      }

      // if nothing was added remove the separator
      if (opts[opts.length - 1].kind === 'separator') opts.pop()
    }

    if (writeRelays.length) {
      // write relays section
      opts.push({ kind: 'separator' })

      for (const r of writeRelays) {
        if (added.includes(r.url)) continue
        opts.push({
          kind: 'relay',
          url: r.url
        })
        added.push(r.url)
      }

      // if nothing was added remove the separator
      if (opts[opts.length - 1].kind === 'separator') opts.pop()
    }

    if (publishTargetItems.length) {
      // hardcoded target sections
      opts.push({ kind: 'separator' })
      for (const t of publishTargetItems) opts.push(t)

      // this will always be added because we forcelly prevent the same relays from being added before
      // by preemptively adding these to the `added` array
    }

    return opts
  }, [
    parentEvents,
    hasExplicitMentions,
    favorites,
    openFromRelays,
    isScheduled,
    nip16Set,
    writeRelays
  ])

  const relaySelectionDescription = useMemo(() => {
    if (composerState.relaySelection.length === 0) {
      return t('No relays selected')
    }
    if (composerState.relaySelection.length === 1) {
      const item = composerState.relaySelection[0]
      if (item.kind === 'item') {
        if (item.id === 'ourWriteRelays') {
          return t('Our outbox relays')
        }
        if (item.id === 'theirReadRelays') {
          return t('Their inbox relays')
        }
      }

      if (item.kind === 'relay') {
        return simplifyUrl(item.url)
      }

      if (item.kind === 'relay-set') {
        return item.relays.length > 2
          ? t('{{count}} relays', { count: item.relays.length })
          : item.relays.length === 2
            ? t('{{relay1}} and {{relay2}}', {
                relay1: simplifyUrl(item.relays[0]),
                relay2: simplifyUrl(item.relays[1])
              })
            : simplifyUrl(item.relays[0])
      }
    }

    const hasOurWriteRelays = composerState.relaySelection.some(
      (item) => item.kind === 'item' && item.id === 'ourWriteRelays'
    )
    const hasTheirReadRelays = composerState.relaySelection.some(
      (item) =>
        (item.kind === 'item' && item.id === 'theirReadRelays') ||
        (item.kind === 'relay' && item.url === 'theirReadRelays')
    )
    const selectedRelayUrls = composerState.relaySelection.flatMap((item) =>
      item.kind === 'relay' ? [item.url] : item.kind === 'relay-set' ? item.relays : []
    )
    const relayCount = selectedRelayUrls.length
    const extraRelayName =
      relayCount === 1 && selectedRelayUrls[0] ? simplifyUrl(selectedRelayUrls[0]) : undefined
    if (hasOurWriteRelays && hasTheirReadRelays) {
      return relayCount > 1
        ? t('Our outbox relays, their inbox relays and {{count}} other relays', {
            count: relayCount
          })
        : relayCount === 1
          ? t('Our outbox relays, their inbox relays and {{relay}}', { relay: extraRelayName })
          : t('Our outbox relays and their inbox relays')
    }
    if (hasOurWriteRelays) {
      return relayCount > 1
        ? t('Our outbox relays and {{count}} other relays', { count: relayCount })
        : relayCount === 1
          ? t('Our outbox relays and {{relay}}', { relay: extraRelayName })
          : t('Our outbox relays')
    }
    if (hasTheirReadRelays) {
      return relayCount > 1
        ? t('Their inbox relays and {{count}} other relays', { count: relayCount })
        : relayCount === 1
          ? t('Their inbox relays and {{relay}}', { relay: extraRelayName })
          : t('Their inbox relays')
    }
    if (relayCount === 2) {
      return t('{{relay1}} and {{relay2}}', {
        relay1: simplifyUrl(selectedRelayUrls[0]),
        relay2: simplifyUrl(selectedRelayUrls[1])
      })
    }
    return t('{{count}} relays', { count: relayCount })
  }, [composerState.relaySelection, t])

  return (
    <div className="space-y-2">
      {parentEvents && parentEvents.length > 1 ? (
        <ScrollArea className="flex max-h-48 flex-col overflow-y-auto rounded-lg border bg-muted/40">
          {parentEvents.map((evt) => (
            <div key={evt.id} className="p-2 sm:p-3 pointer-events-none border-b last:border-none">
              <Note size="small" event={evt} hideParentNotePreview showClientTag />
            </div>
          ))}
        </ScrollArea>
      ) : parentEvents && parentEvents.length === 1 ? (
        <ScrollArea className="flex max-h-48 flex-col overflow-y-auto rounded-lg border bg-muted/40">
          <div className="p-2 sm:p-3 pointer-events-none">
            <Note size="small" event={parentEvents[0]} hideParentNotePreview showClientTag />
          </div>
        </ScrollArea>
      ) : parentExternal ? (
        <div className="pointer-events-none rounded-lg border bg-muted/40 overflow-hidden">
          <WebPreview url={parentExternal} />
        </div>
      ) : null}
      <PostTextarea
        ref={textareaRef}
        text={text}
        setText={setText}
        defaultContent={defaultContent}
        composerStateAtom={composerStateAtom}
        onSubmit={() => post()}
        className="min-h-52"
        onUploadStart={handleUploadStart}
        onUploadEnd={handleUploadEnd}
        onUploadInit={handleUploadInit}
        onServerStatus={handleServerStatus}
        slashComposerContext={slashComposerContext}
      />
      {slashArgProblems.length > 0 && (
        <div className="text-xs text-muted-foreground space-y-0.5">
          {slashArgProblems.map((problem, i) => (
            <div key={i}>
              {problem.type === 'missing'
                ? t('Missing argument: {{name}}', { name: problem.arg.name })
                : problem.type === 'invalid'
                  ? t('Invalid {{type}} for {{name}}: {{value}}', {
                      type: problem.arg.type,
                      name: problem.arg.name,
                      value: problem.value
                    })
                  : t('Unexpected arguments: {{value}}', { value: problem.value })}
            </div>
          ))}
        </div>
      )}
      {uploadProgresses.length > 0 && (
        <div className="space-y-2">
          {uploadProgresses.map(({ file, cancel, servers }, index) => (
            <BlossomUploadChecklist
              key={`${file.name}-${index}`}
              fileName={file.name}
              servers={servers}
              onCancel={() => {
                cancel?.()
                handleUploadEnd(file)
              }}
            />
          ))}
        </div>
      )}
      <div className="flex items-center gap-2">
        {isScheduled ? t('Schedule to') : t('Post to')}
        <RelaySelector
          selected={initialSelected}
          onChange={(rs) => setComposerState((curr) => ({ ...curr, relaySelection: rs }))}
          items={relayOptions}
          hideChangedDot
        >
          <Button type="button" variant="outline" className="px-2 flex-1 max-w-fit justify-start">
            {isScheduled
              ? composerState.relaySelection.length === 0
                ? t('No relays selected')
                : t('Schedule-capable relays')
              : relaySelectionDescription}
          </Button>
        </RelaySelector>
      </div>
      <div className="flex items-center justify-between">
        <div className="flex gap-2 items-center">
          <Uploader
            onUploadSuccess={({ url, file }) => {
              textareaRef.current?.replaceUploadPlaceholder(file, url)
            }}
            onUploadStart={(file, cancel) => {
              textareaRef.current?.insertUploadPlaceholder(file)
              handleUploadStart(file, cancel)
            }}
            onUploadEnd={handleUploadEnd}
            onUploadInit={handleUploadInit}
            onServerStatus={handleServerStatus}
            accept="image/*,video/*,audio/*"
          >
            <Button variant="ghost" size="icon">
              <HugeiconsIcon icon={ImageUpload01Icon} />
            </Button>
          </Uploader>
          {/* I'm not sure why, but after triggering the virtual keyboard,
              opening the emoji picker drawer causes an issue,
              the emoji I tap isn't the one that gets inserted. */}
          {!isTouchDevice() && (
            <EmojiPickerDialog
              onEmojiClick={(emoji) => {
                if (!emoji) return
                textareaRef.current?.insertEmoji(emoji)
              }}
            >
              <Button variant="ghost" size="icon">
                <HugeiconsIcon icon={SmileIcon} />
              </Button>
            </EmojiPickerDialog>
          )}
          <Button
            variant="ghost"
            size="icon"
            title={t('NSFW')}
            className={composerState.nsfw ? 'bg-primary/15 text-primary hover:bg-primary/15' : ''}
            onClick={() => setComposerState((curr) => ({ ...curr, nsfw: !curr.nsfw }))}
          >
            <HugeiconsIcon icon={EyeOffIcon} />
          </Button>
          {parentEvents && parentEvents.length > 0 && (
            <Button
              variant="ghost"
              size="icon"
              title={
                parentEvents[0].kind !== kinds.ShortTextNote ? t('Comment reply') : t('Quiet reply')
              }
              className={
                parentEvents[0].kind !== kinds.ShortTextNote ||
                parentEvents.length > 1 ||
                composerState.quietReply
                  ? 'bg-primary/15 text-primary hover:bg-primary/15'
                  : ''
              }
              onClick={() => {
                if (parentEvents[0].kind !== kinds.ShortTextNote || parentEvents.length > 1)
                  // when replying to anything other than a kind:1
                  // or when replying to multiple events at the same time
                  // force the "quiet reply"/comment
                  return

                setComposerState((curr) => ({ ...curr, quietReply: !curr.quietReply }))
              }}
            >
              <HugeiconsIcon icon={SlowWindsIcon} />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            title={t('Protected (NIP-70)')}
            className={
              composerState.protected ? 'bg-primary/15 text-primary hover:bg-primary/15' : ''
            }
            onClick={() => setComposerState((curr) => ({ ...curr, protected: !curr.protected }))}
          >
            <HugeiconsIcon icon={ShieldMinus} />
          </Button>
          {isPremiumPublisher && (!parentEvents || parentEvents.length === 0) && (
            <Button
              variant="ghost"
              size="icon"
              title={t('Premium (NIP-63)')}
              className={
                composerState.premium
                  ? 'bg-yellow-400/15 text-yellow-600 dark:text-yellow-400 hover:bg-yellow-400/15'
                  : ''
              }
              onClick={() => {
                if (composerState.premium) {
                  setComposerState((curr) => ({ ...curr, premium: false }))
                  return
                }
                const premiumRelays =
                  nip63WriteRelays && nip63WriteRelays.length > 0
                    ? nip63WriteRelays
                    : (nip63FavoriteRelays ?? [])
                setComposerState((curr) => ({
                  ...curr,
                  premium: true,
                  relaySelection: premiumRelays.map((url) => ({ kind: 'relay', url }))
                }))
              }}
            >
              <HugeiconsIcon icon={CrownIcon} />
            </Button>
          )}
          {hasNip16Support && (
            <ScheduleButton
              ref={scheduleAnchorRef}
              scheduledAt={scheduledAt}
              isActive={isScheduled}
              onClick={() => setScheduleOpen((v) => !v)}
            />
          )}
          {hasNip16Support && !isSmallScreen && (
            <Popover open={scheduleOpen} onOpenChange={setScheduleOpen}>
              <PopoverAnchor virtualRef={scheduleAnchorRef} />
              <PopoverContent className="w-auto p-4" align="start" side="top" collisionPadding={16}>
                <SchedulePicker
                  scheduledAt={scheduledAt}
                  onChange={(ts) => {
                    setScheduledAt(ts)
                    if (ts != null) setScheduleOpen(false)
                  }}
                />
              </PopoverContent>
            </Popover>
          )}
          {hasNip16Support && isSmallScreen && (
            <Dialog open={scheduleOpen} onOpenChange={setScheduleOpen}>
              <DialogContent
                className="w-fit max-w-[calc(100vw-2rem)] p-4"
                onOpenAutoFocus={(e) => e.preventDefault()}
              >
                <SchedulePicker
                  scheduledAt={scheduledAt}
                  onChange={(ts) => {
                    setScheduledAt(ts)
                    if (ts != null) setScheduleOpen(false)
                  }}
                />
              </DialogContent>
            </Dialog>
          )}
        </div>
        <div className="flex gap-2 items-center">
          <div className="flex gap-2 items-center max-sm:hidden">
            <Button variant="secondary" disabled={!canPost || posting} onClick={saveForLater}>
              {editingEvent ? t('Save') : t('Save for later')}
            </Button>
            <Button
              variant="secondary"
              onClick={(e) => {
                e.stopPropagation()
                close()
              }}
            >
              {t('Cancel')}
            </Button>
            <Button
              type="submit"
              disabled={!canPost}
              onClick={post}
              className={
                composerState.premium
                  ? 'bg-yellow-400 text-black hover:bg-yellow-500 border-yellow-500'
                  : ''
              }
            >
              {posting && <HugeiconsIcon icon={Loading01Icon} className="animate-spin" />}
              {isScheduled ? t('Schedule') : parentEvents ? t('Reply') : t('Post')}
            </Button>
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-2 sm:hidden">
        <div className="flex gap-2">
          <Button
            className="w-full"
            variant="secondary"
            disabled={!canPost || posting}
            onClick={saveForLater}
          >
            {editingEvent ? t('Save') : t('Save for later')}
          </Button>
          <Button
            className="w-full"
            variant="secondary"
            onClick={(e) => {
              e.stopPropagation()
              close()
            }}
          >
            {t('Cancel')}
          </Button>
        </div>
        <Button
          className={`w-full${composerState.premium ? ' bg-yellow-400 text-black hover:bg-yellow-500 border-yellow-500' : ''}`}
          type="submit"
          disabled={!canPost}
          onClick={post}
        >
          {posting && <HugeiconsIcon icon={Loading01Icon} className="animate-spin" />}
          {isScheduled ? t('Schedule') : parentEvents ? t('Reply') : t('Post')}
        </Button>
      </div>
    </div>
  )

  async function prepare(): Promise<[event: NostrEvent, relayUrls: string[]]> {
    const shouldPublishAsComment =
      !!parentEvents &&
      (parentEvents[0].kind !== kinds.ShortTextNote ||
        composerState.quietReply ||
        (parentEvents && parentEvents.length > 1))

    const tags: string[][] = []
    function addTag(tag: string[]) {
      for (let i = 0; i < tags.length; i++) {
        const t = tags[i]
        if (t[0] === tag[0] && t[1] === tag[1]) return
      }
      tags.push(tag)
    }

    const additionalMentionPubkeys: string[] = []
    function addMention(pk: string) {
      if (pk === pubkey || additionalMentionPubkeys.includes(pk)) return
      additionalMentionPubkeys.push(pk)
    }

    const { content, emojiTags } = transformCustomEmojisInContent(text)
    for (const emoji of emojiTags) addTag(emoji)

    for (const block of parse(content)) {
      switch (block.type) {
        case 'image':
          const tag = getImetaTagByUrl(block.url)
          if (tag) addTag(tag)
          break
        case 'hashtag':
          addTag(['t', block.value])
          break
        case 'reference':
          if ('identifier' in block.pointer) {
            addTag([
              'q',
              `${block.pointer.kind}:${block.pointer.pubkey}:${block.pointer.identifier}`
            ])
          } else if ('id' in block.pointer) {
            const url = getEventHint(block.pointer.id)
            addTag(['q', block.pointer.id, url])
          } else {
            if (block.pointer.pubkey !== pubkey) addTag(['p', block.pointer.pubkey])
            addMention(block.pointer.pubkey)
          }
          break
      }
    }

    if (parentEvents) {
      // root tags
      for (const pe of parentEvents) {
        const rootInfo = await getRoot(pe)

        if (shouldPublishAsComment) {
          // NIP-22
          if (rootInfo && 'identifier' in rootInfo) {
            addTag([
              'A',
              `${rootInfo.kind}:${rootInfo.pubkey}:${rootInfo.identifier}`,
              ...(rootInfo.relays?.[0] ? [rootInfo.relays?.[0]] : [])
            ])
            addTag(['K', `${rootInfo.kind}`])
            addTag(['P', rootInfo.pubkey])
          } else if (rootInfo && 'id' in rootInfo) {
            addTag(['E', rootInfo.id, ...(rootInfo.relays?.[0] ? [rootInfo.relays?.[0]] : [])])
            if (rootInfo.kind) addTag(['K', `${rootInfo.kind}`])
            if (rootInfo.author) addTag(['P', rootInfo.author])
          } else if (rootInfo && 'value' in rootInfo) {
            addTag(['I', rootInfo.value])
            if (rootInfo.value.startsWith('http')) addTag(['K', 'web'])
          } else {
            // root === parent
            addTag(['E', pe.id])
            addTag(['K', `${pe.kind}`])
            addTag(['P', pe.pubkey])
          }
        } else {
          // NIP-10
          if (rootInfo && 'identifier' in rootInfo) {
            addTag([
              'a',
              `${rootInfo.kind}:${rootInfo.pubkey}:${rootInfo.identifier}`,
              rootInfo.relays?.[0] ?? '',
              'root'
            ])
            addMention(rootInfo.pubkey)
          } else if (rootInfo && 'id' in rootInfo) {
            addTag([
              'e',
              rootInfo.id,
              rootInfo.relays?.[0] ?? '',
              'root',
              ...(rootInfo.author ? [rootInfo.author] : [])
            ])
          } else {
            // root === parent (ignore external)
            addTag(['e', pe.id, getEventHint(pe.id)])
          }
        }
      }

      // parent tags
      for (const pe of parentEvents) {
        if (shouldPublishAsComment) {
          // NIP-22
          tags.push(
            ...[
              isReplaceableEvent(pe.kind)
                ? trimTagEnd(['a', getReplaceableCoordinateFromEvent(pe), getEventHint(pe.id)])
                : trimTagEnd(['e', pe.id, getEventHint(pe.id), pe.pubkey]),
              ['k', `${pe.kind}`]
            ]
          )
        } else {
          // NIP-10
          tags.push(
            ...[
              isReplaceableEvent(pe.kind)
                ? ['a', getReplaceableCoordinateFromEvent(pe), getEventHint(pe.id), 'reply']
                : ['e', pe.id, getEventHint(pe.id), 'reply', pe.pubkey]
            ]
          )
        }
        addMention(pe.pubkey)
      }
    } else if (parentExternal) {
      // in this case it will always be NIP-22
      addTag(['I', parentExternal])
      if (parentExternal.startsWith('http')) addTag(['K', 'web'])
      addTag(['i', parentExternal])
      if (parentExternal.startsWith('http')) addTag(['k', 'web'])
    }

    for (const p of additionalMentionPubkeys.values()) addTag(['p', p])
    if (composerState.nsfw) tags.push(['content-warning', 'NSFW'])
    if (composerState.premium) tags.push(['-'], ['nip63'])
    else if (composerState.protected) tags.push(['-'])

    if (!signer) throw new Error('no signer connected')
    const newEvent = await signer.signEvent({
      kind: shouldPublishAsComment ? kinds.Comment : kinds.ShortTextNote,
      content: content,
      tags,
      created_at: scheduledAt ?? now()
    })
    if (!newEvent) throw new Error('sign event returned null')

    const explicitRelayUrls = composerState.relaySelection.flatMap((item) =>
      item.kind === 'relay' ? item.url : item.kind === 'relay-set' ? item.relays : []
    )
    const includeOurWriteRelays =
      scheduledAt == null &&
      composerState.relaySelection.some(
        (item) => item.kind === 'item' && item.id === 'ourWriteRelays'
      )
    const includeTheirReadRelays =
      scheduledAt == null &&
      composerState.relaySelection.some(
        (item) => item.kind === 'item' && item.id === 'theirReadRelays'
      )

    const relayUrls =
      scheduledAt != null && explicitRelayUrls.length > 0
        ? Array.from(new Set(explicitRelayUrls))
        : await determineTargetRelays(newEvent, {
            additionalRelayUrls: explicitRelayUrls,
            includeOurWriteRelays,
            includeTheirReadRelays,
            additionalMentionPubkeys
          })

    return [newEvent, relayUrls]
  }

  async function post(e?: React.MouseEvent) {
    e?.stopPropagation()
    if (!canPost) return

    setPosting(true)
    try {
      const [newEvent, relayUrls] = await prepare()

      // persist immediately so closing the tab during the undo window
      // doesn't lose the event; it can be published later from /pending
      await savePendingEvent(newEvent)

      if (editingEvent) {
        discardPendingEvent(editingEvent.id)
      }

      addEventToCache(newEvent)
      emitEphemeralEvent(newEvent)
      addReplies([newEvent])
      const submittedText = text
      const composerStateSnapshot = composerState
      close()

      const abortController = new AbortController()
      const toastId = `pending-publish-${newEvent.id}`
      const endAt = Date.now() + PENDING_PUBLISH_MS
      const undoPublish = () => {
        abortController.abort()
        void removeEventFromCache(newEvent.id)
        void discardPendingEvent(newEvent.id)
        emitEventRemoved(newEvent.id)
        ;(isReplyNoteEvent(newEvent) ? invalidateReplies : invalidateFeed)()
        toast.dismiss(toastId)
        removePendingPublish(newEvent.id)
        open(submittedText, composerStateSnapshot)
      }

      addPendingPublish(newEvent.id, endAt, undoPublish)
      removeComposerState(defaultContent, parentEvents, openFromRelays)
      toast(<PendingPublishToast endAt={endAt} onUndo={undoPublish} />, {
        id: toastId,
        duration: PENDING_PUBLISH_MS,
        className: '!bg-transparent !border-none !shadow-none !p-0',
        style: { '--width': 'fit-content' } as React.CSSProperties
      })

      void (async () => {
        try {
          await waitForAbortableDelay(PENDING_PUBLISH_MS, abortController.signal)
        } catch {
          // undone
          return
        } finally {
          toast.dismiss(toastId)
          removePendingPublish(newEvent.id)
        }

        try {
          // this takes it out of pending as soon as one relay accepts it
          await publishEventAndDisplay(relayUrls, newEvent)
        } catch (error) {
          // no relay accepted it, keep it pending
          savePendingEvent(newEvent)
          console.warn('saved as pending')
          toast.error(t('Saved as pending'))
          const errors = error instanceof AggregateError ? error.errors : [error]
          for (const err of errors) {
            console.error(err)
          }
          return
        }

        setScheduledAt(null)
        if (
          (newEvent.kind === kinds.ShortTextNote || newEvent.kind === kinds.Comment) &&
          recordNotePublishedDay(newEvent.created_at) &&
          hasUnsavedNsec()
        ) {
          openSaveNsecModal()
        }
      })()
    } catch (error) {
      const errors = error instanceof AggregateError ? error.errors : [error]
      for (const err of errors) {
        toast.error(`${t('Failed to post')}: ${getErrorMessage(err)}`, {
          duration: 10_000
        })
        console.error(err)
      }
      return
    } finally {
      setPosting(false)
    }
  }

  async function saveForLater(e?: React.MouseEvent) {
    e?.stopPropagation()
    if (!canPost) return

    setPosting(true)
    try {
      const [newEvent, _] = await prepare()

      if (editingEvent) {
        discardPendingEvent(editingEvent.id)
      }

      savePendingEvent(newEvent)
      close()
      toast.success(t('Saved for later'))
    } catch (error) {
      console.warn('failed to save draft:', getErrorMessage(error))
      toast.error(`${t('Failed to save draft')}: ${getErrorMessage(error)}`)
    } finally {
      setPosting(false)
    }
  }

  function handleUploadStart(file: UploadFile, cancel: () => void) {
    setUploadProgresses((prev) => {
      const cleaned = prev.filter((item) => {
        if (item.file._uploadId === file._uploadId) return false
        if (item.file.name !== file.name) return true
        return !item.servers.every((s) => s.status.state === 'error')
      })
      return [...cleaned, { file, cancel, servers: [] }]
    })
  }

  function handleUploadInit(info: { file: UploadFile; cancel: () => void; servers: string[] }) {
    setUploadProgresses((prev) =>
      prev.map((item) =>
        item.file._uploadId === info.file._uploadId
          ? {
              ...item,
              servers: info.servers.map((url) => ({
                url,
                status: { state: 'pending' } as ServerStatus
              }))
            }
          : item
      )
    )
  }

  function handleServerStatus(file: UploadFile, server: string, status: ServerStatus) {
    setUploadProgresses((prev) =>
      prev.map((item) =>
        item.file._uploadId === file._uploadId
          ? {
              ...item,
              servers: item.servers.map((s) => (s.url === server ? { ...s, status } : s))
            }
          : item
      )
    )
  }

  function handleUploadEnd(file: UploadFile) {
    setUploadProgresses((prev) => prev.filter((item) => item.file._uploadId !== file._uploadId))
  }
}
